import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Typography } from "@mui/material";
import { CardPageHeader } from "ctech-react-components";
import manHistoryOrders from "assets/supergasbras/images/manHistoryOrders.svg";
import compositionApi from "services/compositionApi";
import { useAuth } from "context/AuthContext";
import * as Styled from "./styles";

interface FormData {
  email: string;
}

interface PaymentReportRequest {
  sendTo: string;
  merchantId: string;
  timezone: string;
}

function ReportsPayment() {
  const { t } = useTranslation();
  const { dealerId } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormData>();

  const getTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const getErrorMessage = (error: any): string => {
    const defaultMessage = "Falha ao solicitar relatório, tente novamente!";
    const statusCode = error?.response?.status;

    if (statusCode === 500) {
      return defaultMessage;
    }

    if (
      error?.response?.data?.errors &&
      Array.isArray(error.response.data.errors)
    ) {
      const firstError = error.response.data.errors[0];
      if (firstError?.message) {
        return firstError.message;
      }
    }

    return defaultMessage;
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      const email = getValues("email");

      const payload: PaymentReportRequest = {
        sendTo: email,
        merchantId: dealerId,
        timezone: getTimezone(),
      };

      await compositionApi.post("/dealers/v1/payment/report", payload);

      toast("Relatório solicitado, aguarde o envio do e-mail!", {
        type: "success",
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      toast(errorMessage, {
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Styled.Container>
      <Styled.Content>
        <CardPageHeader
          urlImage={manHistoryOrders}
          title={
            <Typography variant="h5">
              {t("payments") || "Relatório de Transações do Pagamento Online"}
            </Typography>
          }
        />
        <Styled.StyledCard>
          <Styled.StyledCardContent>
            <Styled.WorkingLine>
              <Styled.Header>
                <Styled.HeaderTitle variant="h5">
                  Relatório de Transações do Pagamento Online
                </Styled.HeaderTitle>
                <Styled.HeaderActions />
              </Styled.Header>

              <Styled.FormRow>
                <Styled.FormField>
                  <Styled.StyledTextField
                    {...register("email", {
                      required: "Campo obrigatório",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "E-mail inválido",
                      },
                    })}
                    label="E-mail para receber relatório"
                    placeholder="Ex: usuario@dominio.com"
                    variant="outlined"
                    fullWidth
                    size="medium"
                    error={!!errors.email}
                    helperText={
                      errors.email?.message ||
                      "O relatório será encaminhado para o email inserido."
                    }
                  />
                </Styled.FormField>
              </Styled.FormRow>

              <Styled.InfoRow>
                <Styled.InfoField>
                  <Styled.InfoText>
                    O relatório mostra cobranças feitas a partir do dia anterior
                    ao do dia da solicitação do relatório, e pode incluir até 31
                    dias de transações.
                    <br />
                    O prazo para envio depende da quantidade, tipos e período
                    das cobranças. Avisaremos por e-mail quando estiver pronto.
                    <br />
                    Caso não tenha recebido verifique a caixa de spam, o e-mail
                    remetende do relatório solicitado é{" "}
                    <Styled.EmailHighlight>
                      noreply@malga.io
                    </Styled.EmailHighlight>{" "}
                    .
                  </Styled.InfoText>
                </Styled.InfoField>
              </Styled.InfoRow>
            </Styled.WorkingLine>
          </Styled.StyledCardContent>

          <Styled.StyledCardActions>
            <Styled.StyledButton
              variant="contained"
              size="large"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Styled.ButtonSpinner size={20} />
                  Carregando...
                </>
              ) : (
                "Solicitar relatório"
              )}
            </Styled.StyledButton>
          </Styled.StyledCardActions>
        </Styled.StyledCard>
      </Styled.Content>
    </Styled.Container>
  );
}

export default ReportsPayment;
